"use client";

import {} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { trackClientError } from "@/lib/client-error";
import { cn } from "@/lib/utils";
import { Home, RotateCcw } from "lucide-react";
import Image from "next/image";
import * as React from "react";
import { useEffect } from "react";
import { MainNavbar } from "./main/main-navbar";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    trackClientError(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <>
            <MainNavbar />
            <div
              className={cn(
                "relative flex min-h-screen w-full flex-col items-center justify-center bg-chalk px-4 pb-20 pt-20",
                this.props.className,
              )}
            >
              {/* Decorative elements */}
              <div className="absolute left-0 top-0 h-32 w-32 animate-pulse rounded-full bg-plaster/10" />
              <div className="absolute bottom-0 right-0 h-48 w-48 animate-pulse rounded-full bg-canary/10" />
              <div className="absolute right-32 top-32 h-24 w-24 animate-pulse rounded-full bg-plaster/5" />
              <div className="absolute bottom-32 left-32 h-40 w-40 animate-pulse rounded-full bg-canary/5" />

              <div className="relative z-10 flex max-w-2xl flex-col items-center space-y-8 text-center">
                {/* Fun illustration */}
                <div className="relative h-48 w-48">
                  <Image
                    src="/images/error-house.svg"
                    alt="House under construction"
                    fill
                    className="animate-bounce-slow"
                  />
                </div>

                <div className="space-y-4">
                  <h1 className="text-4xl font-bold text-smokeyBlack">
                    Oops! We're Doing Some Home Improvements
                  </h1>
                  <p className="text-lg text-darkGray">
                    Just like a home renovation, sometimes things get a little
                    messy. We're working on fixing this up!
                  </p>
                  {this.state.error && (
                    <p className="mt-2 rounded-lg bg-white/50 p-4 font-mono text-sm text-darkGray backdrop-blur-sm">
                      {this.state.error.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="outline"
                    onClick={this.handleReset}
                    className="gap-2 border-plaster text-plaster hover:bg-plaster hover:text-white"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    className="gap-2 bg-canary text-smokeyBlack hover:bg-canary-400"
                  >
                    <Home className="h-4 w-4" />
                    Back to Homepage
                  </Button>
                </div>
              </div>
            </div>
          </>
        )
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundaryWrapper({ children, fallback, className }: Props) {
  useEffect(() => {
    return () => {
      // Clean up any resources or listeners if needed
    };
  }, []);

  return (
    <ErrorBoundary fallback={fallback} className={className}>
      {children}
    </ErrorBoundary>
  );
}

// Add to your CSS or create a new animation in tailwind.config.js
// animation: {
//   'bounce-slow': 'bounce 3s ease-in-out infinite',
// }
